import React, { useState, useEffect } from 'react';
import {
  Box,
  Heading,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  IconButton,
  Button,
  Badge,
  useToast,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Input,
  Select,
  useDisclosure,
  HStack,
  Spinner,
  Text,
} from '@chakra-ui/react';
import { FiEdit2, FiTrash2, FiPlus } from 'react-icons/fi';

const API_BASE = '/api';

function Subscriptions() {
  const toast = useToast();
  const [subscriptions, setSubscriptions] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingSubscription, setEditingSubscription] = useState(null);
  const [subscriptionForm, setSubscriptionForm] = useState({ customer_id: '', product: '', status: 'active' });
  
  const { isOpen: isModalOpen, onOpen: onModalOpen, onClose: onModalClose } = useDisclosure();
  const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure();
  const [subscriptionToDelete, setSubscriptionToDelete] = useState(null);

  const getAuthHeaders = () => ({
    'Content-Type': 'application/json',
    'Authorization': `Token ${localStorage.getItem('token')}`,
  });

  const fetchData = () => {
    Promise.all([
      fetch(`${API_BASE}/subscriptions/`, { headers: getAuthHeaders() }).then(r => r.json()),
      fetch(`${API_BASE}/customers/`, { headers: getAuthHeaders() }).then(r => r.json()),
    ]).then(([subs, custs]) => {
      setSubscriptions(Array.isArray(subs) ? subs : []);
      setCustomers(Array.isArray(custs) ? custs : []);
      setLoading(false);
    }).catch(() => setLoading(false));
  };

  useEffect(() => {
    fetchData();
  }, []);

  const openAddSubscription = () => {
    setEditingSubscription(null);
    setSubscriptionForm({ customer_id: '', product: '', status: 'active' });
    onModalOpen();
  };

  const openEditSubscription = (subscription) => {
    setEditingSubscription(subscription);
    setSubscriptionForm({ 
      customer_id: subscription.customer_id, 
      product: subscription.product, 
      status: subscription.status 
    });
    onModalOpen();
  };

  const handleSaveSubscription = async () => {
    const method = editingSubscription ? 'PUT' : 'POST';
    const url = editingSubscription 
      ? `${API_BASE}/subscriptions/${editingSubscription.id}/` 
      : `${API_BASE}/subscriptions/`;
    
    try {
      const res = await fetch(url, {
        method,
        headers: getAuthHeaders(),
        body: JSON.stringify(subscriptionForm),
      });
      const data = await res.json();
      if (res.ok) {
        fetchData();
        toast({ title: editingSubscription ? 'Subscription updated' : 'Subscription created', status: 'success', duration: 3000 });
        onModalClose();
      } else {
        toast({ title: data.error || 'Error saving subscription', status: 'error', duration: 3000 });
      }
    } catch (e) {
      toast({ title: 'Error saving subscription', status: 'error', duration: 3000 });
    }
  };

  const confirmDelete = (subscription) => {
    setSubscriptionToDelete(subscription);
    onDeleteOpen();
  };

  const handleDelete = async () => {
    if (!subscriptionToDelete) return;
    try {
      const res = await fetch(`${API_BASE}/subscriptions/${subscriptionToDelete.id}/`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });
      if (res.ok) {
        fetchData();
        toast({ title: 'Subscription deleted', status: 'success', duration: 3000 });
      } else {
        toast({ title: 'Error deleting subscription', status: 'error', duration: 3000 });
      }
    } catch (e) {
      toast({ title: 'Error deleting subscription', status: 'error', duration: 3000 });
    }
    onDeleteClose();
    setSubscriptionToDelete(null);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'green';
      case 'canceled': return 'red';
      case 'past_due': return 'orange';
      case 'trialing': return 'blue';
      default: return 'gray';
    }
  };

  if (loading) {
    return <Box textAlign="center" py={20}><Spinner size="xl" color="teal.500" /></Box>;
  }

  return (
    <Box>
      <HStack justify="space-between" mb={6}>
        <Heading size="lg">Subscriptions</Heading>
        <Button leftIcon={<FiPlus />} colorScheme="teal" onClick={openAddSubscription}>
          Add Subscription
        </Button>
      </HStack>

      <Box bg="white" p={6} borderRadius="lg" boxShadow="md">
        <Table variant="simple">
          <Thead>
            <Tr>
              <Th>Customer</Th>
              <Th>Stripe ID</Th>
              <Th>Product</Th>
              <Th>Status</Th>
              <Th>Created</Th>
              <Th>Actions</Th>
            </Tr>
          </Thead>
          <Tbody>
            {subscriptions.map(subscription => (
              <Tr key={subscription.id}>
                <Td fontWeight="medium">{subscription.customer_email}</Td>
                <Td><Text fontSize="sm" fontFamily="mono">{subscription.stripe_id}</Text></Td>
                <Td>{subscription.product}</Td>
                <Td>
                  <Badge colorScheme={getStatusColor(subscription.status)}>
                    {subscription.status}
                  </Badge>
                </Td>
                <Td>{new Date(subscription.created_at).toLocaleDateString()}</Td>
                <Td>
                  <HStack spacing={2}>
                    <IconButton
                      icon={<FiEdit2 />}
                      size="sm"
                      variant="ghost"
                      onClick={() => openEditSubscription(subscription)}
                      aria-label="Edit"
                    />
                    <IconButton
                      icon={<FiTrash2 />}
                      size="sm"
                      variant="ghost"
                      colorScheme="red"
                      onClick={() => confirmDelete(subscription)}
                      aria-label="Delete"
                    />
                  </HStack>
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
        {subscriptions.length === 0 && (
          <Text color="gray.500" textAlign="center" py={8}>No subscriptions found</Text>
        )}
      </Box>

      {/* Add/Edit Modal */}
      <Modal isOpen={isModalOpen} onClose={onModalClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{editingSubscription ? 'Edit Subscription' : 'Add Subscription'}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {!editingSubscription && (
              <FormControl mb={4}>
                <FormLabel>Customer</FormLabel>
                <Select
                  value={subscriptionForm.customer_id}
                  onChange={(e) => setSubscriptionForm({ ...subscriptionForm, customer_id: e.target.value })}
                  placeholder="Select customer"
                >
                  {customers.map(c => (
                    <option key={c.id} value={c.id}>{c.email}</option>
                  ))}
                </Select>
              </FormControl>
            )}
            <FormControl mb={4}>
              <FormLabel>Product ID</FormLabel>
              <Input
                value={subscriptionForm.product}
                onChange={(e) => setSubscriptionForm({ ...subscriptionForm, product: e.target.value })}
                placeholder="price_..."
              />
            </FormControl>
            <FormControl>
              <FormLabel>Status</FormLabel>
              <Select
                value={subscriptionForm.status}
                onChange={(e) => setSubscriptionForm({ ...subscriptionForm, status: e.target.value })}
              >
                <option value="active">Active</option>
                <option value="canceled">Canceled</option>
                <option value="past_due">Past Due</option>
                <option value="trialing">Trialing</option>
                <option value="unpaid">Unpaid</option>
              </Select>
            </FormControl>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onModalClose}>Cancel</Button>
            <Button colorScheme="teal" onClick={handleSaveSubscription}>Save</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal isOpen={isDeleteOpen} onClose={onDeleteClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Delete Subscription</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            Are you sure you want to delete this subscription? This action cannot be undone.
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onDeleteClose}>Cancel</Button>
            <Button colorScheme="red" onClick={handleDelete}>Delete</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}

export default Subscriptions;
